import { TODO } from '@b2b/types/core';
import { AutorenewalType, Membership } from '@b2b/types/membership';
import { SubscriptionDetail } from '@b2b/types/subscriptions';
import angular, { IFilterService, IPromise, IQService, IWindowService } from 'angular';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../../api/types';

interface CartMembershipsState {
  subscription: {
    status: 'idle' | 'loading' | 'error' | 'success';
    data: SubscriptionDetail | null;
  };
  hasMemberships: boolean; 
  hasNewMembership: boolean;
}

function createInitialState(): CartMembershipsState {
  return {
    subscription: { status: 'idle', data: null },
    hasMemberships: false,
    hasNewMembership: false,
  };
}

export class CartMembershipsService {
  static $inject = ['$q', '$filter', '$window', 'ApiService', 'MEMBERSHIP_ENUMS', 'MembershipService'];
  private stateSubject = new BehaviorSubject<CartMembershipsState>(createInitialState());
  state$ = this.stateSubject.asObservable();
  model: any[] = [];
  parent = {} as {
    getMembershipItem: () => any;
    getItems: () => any[];
    getItemByType: (itemType: any) => any;
    setItems: (items: any[], resetCoupons: boolean) => void;
    updateCart: () => Promise<any>;
    updateOrderContact: (membershipItem?: any) => void;
    getOrderContact: () => any;
    updateItem: (eventItem: any) => any;
    addItem: (eventItem: any) => any;
    removeItem: (itemId: any, skipGetCart: boolean) => any;
  };

  membershipLevels = {};

  customersDuplicates: any | null = null;
  membershipCartData: any | null = null;

  customerMemberships: TODO[] = [];

  subscriptionData: any | null = null;

  restrictMembershipsToOne = true;
  ENUMS = this.MEMBERSHIP_ENUMS;

  constructor(
    private $q: IQService,
    private $filter: IFilterService,
    private $window: IWindowService,
    private ApiService: ApiService,
    private MEMBERSHIP_ENUMS,
    private MembershipService: TODO
  ) {}

  init(parent) {
    this.parent = parent;
    this.initCustomerDuplicates();
    return this;
  }

  initCustomerDuplicates() {
    this.customersDuplicates = this.getCustomerDuplicatesFromStorage();
    this.membershipCartData = this.getMembershipCartDataFromStorage();
  }

  getNormalizedAction(action) {
    return action.toLowerCase();
  }

  getSubscriptionData(membership) {
    const currentMembership = membership || null;
    const membershipLevels = currentMembership.level ? [currentMembership.level] : [];

    if (this.hasAutorenewMembershipsInCart(membershipLevels) && !this.subscriptionData) {
      this.stateSubject.next({
        ...this.stateSubject.value,
        subscription: { status: 'loading', data: null },
      });

      this.ApiService.subscriptions
        .detail(membership.membershipInfo.membershipId)
        .then((response) => {
          this.subscriptionData = response.data.payload;

          this.stateSubject.next({
            ...this.stateSubject.value,
            subscription: { status: 'success', data: response.data.payload },
          });
        })
        .catch((response) => {
          this.subscriptionData = null;

          this.stateSubject.next({
            ...this.stateSubject.value,
            subscription: { status: 'error', data: null },
          });
        });
    }
  }

  lifecycle(
    actionTypes: string[],
    membership: { membershipId: number; membershipCategory?: string; membershipName?: string }
  ): IPromise<TODO> {
    const q = this.$q.defer<TODO>();
    const eligibilityTypes = actionTypes.join();
    const title = 'Membership is not eligible';

    this.ApiService.membership.eligibility
      .listByMembershipCategory({
        membershipId: String(membership.membershipId),
        saleChannels: 'insideSalesIndividual',
        actions: eligibilityTypes,
        summarize: 0,
      })
      .then((response) => {
        const eligibility = response.data.payload;
        const eligibleLevels = eligibility.membershipOptions.eligibleMembershipLevels;

        const errorMessage = [
          'The',
          membership.membershipCategory,
          'membership for',
          membership.membershipName,
          'is not eligible to',
          actionTypes.length > 2 ? 'renew' : actionTypes.join('/'),
        ]
          .filter(Boolean)
          .join(' ');

        if (eligibleLevels.length) {
          let _actionAvailable = false;

          angular.forEach(eligibleLevels, (eligibleLevel) => {
            if (eligibilityTypes.indexOf(eligibleLevel.type) > -1 && eligibleLevel.targetLevels.length) {
              _actionAvailable = true;
            }
          });

          if (_actionAvailable) {
            q.resolve(response);
          } else {
            q.reject({
              data: { title: title, error: errorMessage },
            });
          }
        } else {
          q.reject({
            data: { title: title, error: errorMessage },
          });
        }
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  hasPricepointMatch(
    membership: any,
    eligibility: any,
    type?: any
  ): {
    level: any;
    offering: any;
    pricepoint: any;
    type: any;
  } | null {
    const eligibleLevels = eligibility.membershipOptions.eligibleMembershipLevels;
    let result: {
      level: any;
      offering: any;
      pricepoint: any;
      type: any;
    } | null = null;
    let eligibilityType = type;

    if (!type) {
      angular.forEach(eligibleLevels, (eligibleLevel) => {
        //since it can be any of these renew types
        switch (eligibleLevel.type) {
          case this.ENUMS.renew[0]:
          case this.ENUMS.renew[3]:
          case this.ENUMS.renew[6]:
            eligibilityType = eligibleLevel.type;
            break;
        }
      });
    }

    if (!eligibilityType) {
      eligibilityType = this.ENUMS.renew[0];
    }

    const filteredEligibility = eligibleLevels.filter((eligibleLevel) => {
      return eligibleLevel.type === eligibilityType;
    });

    if (filteredEligibility.length) {
      angular.forEach(filteredEligibility[0].targetLevels, (level) => {
        if (level.id === membership.categoryId) {
          angular.forEach(level.offerings, (offering) => {
            if (offering.id === membership.offeringId) {
              if (offering.priceLists instanceof Array && offering.priceLists.length) {
                angular.forEach(offering.priceLists[0].prices, (price) => {
                  if (price.personType.id === membership.pricePointId) {
                    result = {
                      level: level,
                      offering: offering,
                      pricepoint: price,
                      type: eligibilityType,
                    };
                  }
                });
              }
            }
          });
        }
      });
    }

    return result;
  }

  hasPricepointMatchForLevel(membership, level) {
    let result = false;

    if (level && level.id === membership.categoryId) {
      angular.forEach(level.offerings, (offering) => {
        if (offering.id === membership.offeringId) {
          if (offering.priceLists instanceof Array && offering.priceLists.length) {
            angular.forEach(offering.priceLists[0].prices, (price) => {
              if (price.personType.id === membership.pricePointId) {
                result = true;
              }
            });
          }
        }
      });
    }

    return result;
  }

  hasOfferingMatchForLevel(membership, level) {
    let result = false;

    if (level && level.id === membership.categoryId) {
      angular.forEach(level.offerings, (offering) => {
        if (offering.id === membership.offeringId) {
          if (offering.priceLists instanceof Array && offering.priceLists.length) {
            result = true;
          }
        }
      });
    }

    return result;
  }

  levelDetail(level: { id: number }): IPromise<TODO> {
    const q = this.$q.defer<TODO>();

    this.ApiService.membership.categories.detail(String(level.id)).then(
      (response) => {
        q.resolve(response);
      },
      (response) => {
        q.reject(response);
      }
    );

    return q.promise;
  }

  addToCart(membership) {
    const q = this.$q.defer();
    const existingItem = this.restrictMembershipsToOne
      ? this.parent.getMembershipItem()
      : this.parent.getItemByType(membership.itemType);
    const membershipItem = {
      quantity: 1,
      itemType: membership.itemType,
      membershipInfo: {
        membershipId: membership.membershipId,
        membershipCategoryId: membership.categoryId,
        membershipCategoryName: membership.membershipCategory,
        membershipOfferingId: membership.offeringId,
        membershipOfferingName: membership.offeringName,
        pricePointId: membership.pricePointId,
        pricePointName: membership.pricePointName,
        orderContactId: membership.orderContactId,
        term: membership.term,
        isAutorenew: membership.isAutorenew,
      },
      amount: membership.amount,
      membershipId: membership.membershipId, // the membership used to purchase this order item
    } as any; // TODO: remove any

    if (membership.membershipId) {
      this.ApiService.membership.detail(membership.membershipId).then(
        (response) => {
          const m = response.data.payload;

          if (m && m.cardholders) {
            m.cardholders = m.cardholders.sort(this.sortByType);
            m.cardholders = this.processCardholders(m.cardholders);
            membershipItem.membershipInfo.membershipCards = m.cardholders;
            this.parent.updateOrderContact(membershipItem);
          }

          if (existingItem) {
            membershipItem.itemId = existingItem.itemId;
            if (existingItem.membershipInfo && existingItem.membershipInfo.membershipCards instanceof Array) {
              membershipItem.membershipInfo.membershipCards = existingItem.membershipInfo.membershipCards;
            }
            this.parent.updateItem(membershipItem);
          } else {
            this.parent.addItem(membershipItem);
          }

          this.parent.updateCart().then(
            (response) => {
              q.resolve(response);
            },
            (response) => {
              q.reject(response);
            }
          );
        },
        (response) => {
          q.reject(response);
        }
      );
    } else {
      if (existingItem) {
        membershipItem.itemId = existingItem.itemId;
        if (existingItem.membershipInfo && existingItem.membershipInfo.membershipCards instanceof Array) {
          membershipItem.membershipInfo.membershipCards = existingItem.membershipInfo.membershipCards;
        }
        this.parent.updateItem(membershipItem);
      } else {
        this.parent.addItem(membershipItem);
      }

      this.parent.updateCart().then(
        (response) => {
          q.resolve(response);
        },
        (response) => {
          q.reject(response);
        }
      );
    }

    return q.promise;
  }

  /**
   * Updates only the order contact for the cart,
   * Use this is if there aren't items to add to the cart yet.
   * If there are items with +ve quantities, use addToCart instead.
   */
  updateOrderContact(membership) {
    const q = this.$q.defer();
    let membershipItem;
    const orderContactId = membership ? membership.orderContactId : null;
    const cartOrderContact = this.parent.getOrderContact();
    const cartOrderContactId = cartOrderContact ? cartOrderContact.customerId : null;
    const updateOrderContactAndCart = (membershipItem?: any) => {
      this.parent.updateOrderContact(membershipItem);

      this.parent.updateCart().then(
        (response) => {
          q.resolve(response);
        },
        (response) => {
          q.reject(response);
        }
      );
    };

    if (cartOrderContactId && (!orderContactId || orderContactId !== cartOrderContactId)) {
      //if you're changing the order contact, then clear the cart
      this.parent.setItems([], true);
    }

    if (membership) {
      membershipItem = {
        membershipInfo: {
          membershipCards: membership.cardholders,
          orderContactId: membership.orderContactId,
        },
      };

      if (membership.cardholders instanceof Array && membership.cardholders.length) {
        updateOrderContactAndCart(membershipItem);
      } else if (membership.membershipId) {
        this.ApiService.membership.detail(membership.membershipId).then(
          (response) => {
            const m = response.data.payload;

            if (m && m.cardholders) {
              m.cardholders = m.cardholders.sort(this.sortByType);
              membershipItem.membershipInfo.membershipCards = m.cardholders;
            }
            updateOrderContactAndCart(membershipItem);
          },
          (response) => {
            q.reject(response);
          }
        );
      }
    } else {
      updateOrderContactAndCart();
    }

    return q.promise;
  }

  sortByType(a, b) {
    const aType = a.cardType || '';
    const bType = b.cardType || '';
    let result = 0;
    if (aType < bType) {
      result = -1;
    } else if (aType > bType) {
      result = 1;
    }
    return result;
  }

  setModel(data: any) {
    const found = data.length ? this.membershipLevels[data[0].membershipInfo.membershipCategoryId] : null;

    if (found && found.itemType === data[0].itemType) {
      data[0].level = found.level;
    }

    this.model = data;

    angular.forEach(this.model, (membership) => {
      angular.forEach(this.getCardholders(membership), (cardholder) => {
        cardholder.membershipId = membership.membershipInfo.membershipId;
        cardholder.address = {
          country: cardholder.country,
          city: cardholder.city,
          streetAddress1: cardholder.streetAddress1,
          streetAddress2: cardholder.streetAddress2,
          state: cardholder.state,
          zipCode: cardholder.zipCode,
        };
      });
    });

    this.updateMembershipState();
  }

  updateMembershipState () {
    this.stateSubject.next({
      ...this.stateSubject.value,
      hasMemberships: !!this.model.length,
      hasNewMembership: this.hasNewMembership()
    });
  }

  getModel() {
    return this.model;
  }

  setModelForced(model) {
    this.model = model;
    this.updateMembershipState();
  }

  resetModel() {
    this.model = [];
    this.membershipLevels = {};
    this.subscriptionData = null;
    this.clearCustomerData();
    this.updateMembershipState();
  }

  getById(id) {
    let result: { index: number; membership: any } | false = false;

    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].membershipInfo.membershipCategoryId === id) {
        result = {
          membership: this.model[i],
          index: i,
        };
        break;
      }
    }
    return result;
  }

  isMembershipExpired(cardHolder) {
    let result = false;

    if (cardHolder && cardHolder.membershipStanding) {
      result = cardHolder.membershipStanding !== 'active';
    } else if (cardHolder && cardHolder.cardStatus) {
      //fallback to cardholder status if membershipStanding is not available -- this should be very rare
      result = cardHolder.cardStatus !== 'active';
    }

    return result;
  }

  setOfferings(id, levels) {
    const found = this.getById(id);

    if (found) {
      this.model[found.index].offerings = levels;
    }
  }

  setLevel(id, level) {
    const found = this.getById(id);
    let processedLevel;
    if (found) {
      processedLevel = this.processLevel(level);
      this.model[found.index].level = processedLevel;
      this.membershipLevels[level.id] = { level: processedLevel, itemType: this.model[found.index].itemType };
      this.model[found.index].selectedOffering = this.selectOffering(
        this.model[found.index].level.pricepoints,
        this.model[found.index]
      );
    }
  }

  processLevel(level) {
    const result: {
      offeringId: any;
      pricepointId: any;
      id: any;
      displayName: any;
      amount: any;
      unitPrice: any;
      retailUnitPrice: any;
      term: any;
      auxGroup: any;
      actions: any;
    }[] = [];

    if (this.hasOfferings(level)) {
      angular.forEach(level.offerings, (offering) => {
        if (
          offering.priceLists instanceof Array &&
          offering.priceLists.length &&
          offering.priceLists[0].prices instanceof Array
        ) {
          angular.forEach(offering.priceLists[0].prices, (price) => {
            result.push({
              offeringId: offering.id,
              pricepointId: price.personType.id,
              id: this.getPricepointSelectionId(offering.id, price.personType.id),
              displayName: this.getPricepointDisplayName(offering, price.personType.name),
              amount: price.price,
              unitPrice: price.discountedPrice,
              retailUnitPrice: price.price,
              term: offering.term,
              auxGroup: offering.auxGroup,
              actions: offering.actions,
            });
          });
        }
      });
      level.pricepoints = result;
    }

    return level;
  }

  processCardholders(cardholders) {
    if (cardholders instanceof Array) {
      angular.forEach(cardholders, (cardholder) => {
        if (cardholder.cardStatus && cardholder.cardStatus === 'dropped') {
          cardholder.excludeCardOnMemberCreation = true;
        }
      });
    }
    return cardholders;
  }

  selectOffering(pricepoints, membership) {
    let selectedOffering;

    angular.forEach(pricepoints, (pricepoint) => {
      if (
        pricepoint.offeringId === membership.membershipInfo.membershipOfferingId &&
        pricepoint.pricepointId === membership.membershipInfo.pricePointId
      ) {
        selectedOffering = pricepoint;
      }
    });

    if (!selectedOffering && pricepoints.length) {
      selectedOffering = pricepoints[0];
    }

    return selectedOffering;
  }

  hasOfferings(level) {
    return level && level.offerings && level.offerings.length;
  }

  getPricepointSelectionId(offeringId, pricepointId) {
    return offeringId + '-' + pricepointId;
  }

  getPricepointDisplayName(offering, pricePointName) {
    return [offering.name, pricePointName].join(', ');
  }

  getPrimaryCardholder(membership) {
    const result = this.$filter('filter')(
      this.getCardholders(membership),
      angular.bind(this, this.isPrimaryCardholder)
    );

    return result.length ? result[0] : null;
  }

  isPrimaryCardholder(cardholder) {
    return this.isCardType(cardholder, 'primary');
  }

  isSecondaryCardholder(cardholder) {
    return this.isCardType(cardholder, 'secondary');
  }

  isCardType(cardholder, cardType) {
    return cardholder.cardType === cardType;
  }

  isCardholderACustomer(cardholder, customerId) {
    let result = cardholder && !!cardholder.customerId;

    if (result && customerId) {
      //We're using == here intentionally so as to not enforce types
      result = result && cardholder.customerId == customerId;
    }

    return result;
  }

  /*
   * The order contact is determined as follows:
   * - The cardholder whose customerId has been provided
   * - If the customerId is not provided, it is the primary cardholder who is also a customer
   * - If a primary cardholder customer is not available, it is the first secondary cardholder who is also a customer
   */
  getOrderContact(cardholders: any, customerId: any) {
    let result: any | null = null;
    let primaryCardholders;
    let secondaryCardholders;

    if (cardholders instanceof Array) {
      result = this.getCustomer(cardholders, customerId);

      if (!result) {
        primaryCardholders = this.$filter('filter')(cardholders, angular.bind(this, this.isPrimaryCardholder));

        if (primaryCardholders) {
          this.getCustomer(primaryCardholders, customerId);
        } else {
          secondaryCardholders = this.$filter('filter')(cardholders, angular.bind(this, this.isSecondaryCardholder));
          this.getCustomer(secondaryCardholders, customerId);
        }
      }
    }

    return result;
  }

  getCustomer(cardholders: any[], customerId: any) {
    let result: any | null = null;

    angular.forEach(cardholders, (cardholder) => {
      if (!result && this.isCardholderACustomer(cardholder, customerId)) {
        result = cardholder;
      }
    });

    return result;
  }

  getCardholder(membership, id) {
    let result = {};

    result = this.$filter('filter')(this.getCardholders(membership), (cardholder) => {
      return cardholder.id === id;
    })[0];

    return result;
  }

  getCardholders(membership: any) {
    let result: any[] = [];

    if (membership && membership.membershipInfo && membership.membershipInfo.membershipCards) {
      result = membership.membershipInfo.membershipCards;
    }

    return result;
  }

  getDiscountMembershipLevelIds(newOrRejoinOnly) {
    const membership = this.parent.getMembershipItem();
    const membershipLevelIds: any[] = [];

    if (membership) {
      const action = this.MembershipService.getActionFromPurchaseType(membership.itemType);

      switch (action) {
        case this.ENUMS.renew[3]:
        //falls through
        case this.ENUMS.renew[4]:
        //falls through
        case this.ENUMS.renew[5]:
        //falls through
        case this.ENUMS.renew[6]:
        //falls through
        case this.ENUMS.renew[7]:
        //falls through
        case this.ENUMS.renew[8]:
        //falls through
        case this.ENUMS.eligibilityTypes[0]:
          membershipLevelIds.push(membership.membershipInfo.membershipCategoryId);
          break;
      }
    }

    return membershipLevelIds;
  }

  hasNewMembership() {
    return this.parent.getItemByType(this.MembershipService.purchaseTypes['new_memberships'].name);
  }

  /**
   * Returns array of upgrade or downgrade memberships in cart
   */
  getUpgradeDowngradeMembership() {
    const eligibilityTypes = this.ENUMS.upgradeDowngrade.concat(this.ENUMS.upgradeDowngradeCart);
    return this.parent.getItems().filter((item) => {
      return eligibilityTypes.includes(item.itemType);
    });
  }

  /**
   * returns boolean in case there are memberships in cart
   */
  hasMemberships() {
    const eligibilityTypes = this.ENUMS.eligibilityTypes.concat(this.ENUMS.eligibilityTypesCart);
    return this.parent.getItems().some((item) => {
      return eligibilityTypes.includes(item.itemType);
    });
  }

  isNewMembership(type) {
    return type === this.MembershipService.purchaseTypes['new_memberships'].name;
  }

  setMembershipCartData(membershipCartData) {
    this.$window.sessionStorage.setItem('membershipCartData', JSON.stringify(membershipCartData));
    this.membershipCartData = membershipCartData;
  }

  getMembershipCartData() {
    return this.membershipCartData;
  }

  getMembershipCartDataFromStorage() {
    const membershipCardDataJson = this.$window.sessionStorage.getItem('membershipCartData');

    if (membershipCardDataJson) {
      return JSON.parse(membershipCardDataJson);
    }

    return null;
  }

  clearCustomerMemberships() {
    this.customerMemberships = [];
  }

  setCustomerMemberships(customerMemberships) {
    this.customerMemberships = customerMemberships;
  }

  getCustomerMemberships() {
    return this.customerMemberships;
  }

  setCustomerDuplicates(customersDuplicates) {
    this.$window.sessionStorage.setItem('customersDuplicates', JSON.stringify(customersDuplicates));
    this.customersDuplicates = customersDuplicates;
  }

  getCustomerDuplicates() {
    return this.customersDuplicates;
  }

  getCustomerDuplicatesFromStorage() {
    const customersDuplicatesJson = this.$window.sessionStorage.getItem('customersDuplicates');

    if (customersDuplicatesJson) {
      return JSON.parse(customersDuplicatesJson);
    }

    return null;
  }

  getDupesStatus(customerDuplicate): 'unknown' | 'potential_match' | 'selected_existing_customer' | 'none_match' {
    const hasPossibleDuplicates =
      customerDuplicate.possibleDuplicates && customerDuplicate.possibleDuplicates.length > 0;
    const isUnknownStatus = customerDuplicate.dupesStatus == null || customerDuplicate.dupesStatus === 'unknown';

    if (!hasPossibleDuplicates) {
      return 'unknown';
    }

    if (hasPossibleDuplicates && isUnknownStatus) {
      return 'potential_match';
    }

    return customerDuplicate.dupesStatus;
  }

  clearCustomerData() {
    if (this.$window.sessionStorage.getItem('customersDuplicates')) {
      this.$window.sessionStorage.removeItem('customersDuplicates');
      this.customersDuplicates = null;
    }

    if (this.$window.sessionStorage.getItem('membershipCartData')) {
      this.$window.sessionStorage.removeItem('membershipCartData');
      this.membershipCartData = null;
    }
  }

  getCardholderInfo() {
    const currentMembership = this.membershipCartData;
    let primaryCardholder: any;
    let result: any;

    if (currentMembership) {
      primaryCardholder = this.getPrimaryCardholderInfo(currentMembership);
      const cardHolder = angular.copy(primaryCardholder);
      cardHolder.membershipLevel = currentMembership.level.name;
      cardHolder.nameOnCard = currentMembership.membershipInfo.membershipCards[0].name;
      result = {
        cardType: primaryCardholder.cardType,
        cardHolder: cardHolder,
      };
    }

    return angular.copy(result);
  }

  getPrimaryCardholderInfo(membership) {
    let memberships = [];

    if (membership && membership.membershipInfo.membershipCards instanceof Array) {
      memberships = membership.membershipInfo.membershipCards.filter((cardholder) => {
        return cardholder.cardType === 'primary';
      });
    }

    return memberships.length ? memberships[0] : null;
  }

  findSelectedOfferingFromMembershipLevels(membershipLevels: object[], membership: any) {
    const membershipLevel = this.extractMembershipLevel(membershipLevels, membership);
    const offerings = (membershipLevel && membershipLevel.offerings) || [];

    return offerings.find((offering) => {
      return offering.id === membership.membershipInfo.membershipOfferingId;
    });
  }

  extractMembershipLevel(membershipLevels, membership) {
    const actionType = this.MembershipService.getActionFromPurchaseType(membership.itemType);
    let membershipLevel;
    let eligibleMembershipLevel;
    let targetLevel;
    const membershipCategoryId = membership.membershipInfo.membershipCategoryId;

    for (let i = 0; i < membershipLevels.length; i++) {
      membershipLevel = membershipLevels[i];

      /**
       * `membershipLevels` have a different structure while renewing/upgrading an existing membership
       */
      if (membershipLevel.membershipOptions) {
        for (let j = 0; j < membershipLevel.membershipOptions.eligibleMembershipLevels.length; j++) {
          eligibleMembershipLevel = membershipLevel.membershipOptions.eligibleMembershipLevels[j];

          if (eligibleMembershipLevel.type !== actionType) {
            continue;
          }

          for (let k = 0; k < eligibleMembershipLevel.targetLevels.length; k++) {
            targetLevel = eligibleMembershipLevel.targetLevels[k];

            if (targetLevel.id === membershipCategoryId) {
              return targetLevel;
            }
          }
        }
      } else if (membershipLevel.id === membershipCategoryId) {
        return membershipLevel;
      }
    }

    return undefined;
  }

  isAutorenewType(params: { autorenewalType: AutorenewalType | undefined }) {
    return params.autorenewalType === 'required' || params.autorenewalType === 'optional';
  }

  isAutorenewMembership(membershipLevels: object[], membership: Pick<Membership, 'membershipInfo'>) {
    const selectedOffering = this.findSelectedOfferingFromMembershipLevels(membershipLevels, membership);
    const isAutorenew = this.isAutorenewType({ autorenewalType: selectedOffering?.autorenewalType });
    const enabled = membership.membershipInfo.isAutorenew === true;

    return isAutorenew && enabled;
  }

  hasAutorenewMemberships(membershipLevels: object[]) {
    return this.model.some((membership) => {
      return this.isAutorenewMembership(membershipLevels, membership);
    });
  }

  hasAutorenewMembershipsInCart(membershipLevels: object[]) {
    return this.model.some((membership) => {
      const selectedOffering = this.findSelectedOfferingFromMembershipLevels(membershipLevels, membership);
      return this.isAutorenewType({
        autorenewalType: selectedOffering ? selectedOffering.autorenewalType : null,
      });
    });
  }

  fetchMembershipLevelDetail(membership: {
    itemType: TODO;
    membershipInfo: TODO;
    membershipId: number;
    membershipCategory: string;
    membershipName: string;
  }): IPromise<TODO | undefined> {
    const actionType = this.MembershipService.getActionFromPurchaseType(membership.itemType);

    if (membership.membershipInfo.membershipId && actionType) {
      return this.lifecycle([actionType], membership).then((response) => {
        return response.data.payload;
      });
    }

    if (membership.membershipInfo.membershipCategoryId) {
      return this.levelDetail({ id: membership.membershipInfo.membershipCategoryId }).then((response) => {
        return response.data.payload;
      });
    }

    return Promise.resolve(undefined);
  }

  send(event: { type: 'RESET' }) {
    switch (event.type) {
      case 'RESET':
        this.stateSubject.next(createInitialState());
        break;

      default:
        break;
    }
  }
}

export const CartMembershipsServiceModule = angular
  .module('acme.common.service.cart.memberships', ['acme.common.service.api', 'acme.common.service.membership'])
  .value('MEMBERSHIP_ENUMS', {
    newmembership: ['new_memberships'],
    renew: [
      'renew',
      'renewUpgrade',
      'renewDowngrade',
      'postTermRenew',
      'postTermRenewUpgrade',
      'postTermRenewDowngrade',
      'rejoin',
      'rejoinUpgrade',
      'rejoinDowngrade',
    ],
    upgradeDowngrade: ['upgrade', 'downgrade'],
    cancel: ['cancel'],
    eligibilityTypes: [
      'new_memberships',
      'renew',
      'renewUpgrade',
      'renewDowngrade',
      'postTermRenew',
      'postTermRenewUpgrade',
      'postTermRenewDowngrade',
      'rejoin',
      'rejoinUpgrade',
      'rejoinDowngrade',
      'upgrade',
      'downgrade',
      'cancel',
    ],
    eligibilityTypesCart: [
      'MembershipPurchase',
      'MembershipUpgrade',
      'MembershipRenewal',
      'MembershipPostTermRenewal',
      'MembershipRejoin',
      'MembershipRenewalUpgrade',
      'MembershipPostTermRenewalUpgrade',
      'MembershipRejoinUpgrade',
      'MembershipRenewalDowngrade',
      'MembershipPostTermRenewalDowngrade',
      'MembershipRejoinDowngrade',
      'MembershipDowngrade',
      'MembershipCancel',
    ],
    upgradeDowngradeCart: ['MembershipUpgrade', 'MembershipDowngrade'],
  })
  .service('CartMembershipsService', CartMembershipsService).name;
